import React, { useState, useEffect } from 'react';
import { callApi } from './api';
import { QRCodeSVG } from 'qrcode.react';

// Helper to generate a human-friendly name from MAC address
const generateAlias = (mac) => {
  if (!mac) return "Ukjent-Enhet";
  const parts = mac.split(':');
  if (parts.length < 2) return mac;

  const nato = ["Alfa", "Bravo", "Charlie", "Delta", "Echo", "Foxtrot", "Golf", "Hotel", "India", "Juliett", "Kilo", "Lima", "Mike", "November", "Oscar", "Papa", "Quebec", "Romeo", "Sierra", "Tango", "Uniform", "Victor", "Whiskey", "Xray", "Yankee", "Zulu"];
  
  const lastByte = parseInt(parts[parts.length - 1], 16) || 0;
  const secondLastByte = parseInt(parts[parts.length - 2], 16) || 0;
  
  const word = nato[secondLastByte % nato.length];
  const number = lastByte.toString().padStart(3, '0');
  
  return `${word}-${number}`;
};

// Form for registering a new physical device
function RegisterDeviceForm({ onDeviceRegistered, onCancel }) {
  const [deviceId, setDeviceId] = useState('');
  const [model, setModel] = useState('ESP32-C6');
  const [batchId, setBatchId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await callApi('registerDeviceV2', { deviceId: deviceId.trim().toUpperCase(), model, batchId });
      if (onDeviceRegistered) onDeviceRegistered();
    } catch (err) {
      setError('Kunne ikke registrere enhet: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <form onSubmit={handleSubmit} style={{ background: 'var(--background-light)', color: 'var(--text-light)', padding: '25px', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '15px', width: '400px', border: '1px solid var(--border-color)' }}>
        <h4 style={{ margin: '0 0 10px 0' }}>Registrer Ny Fysisk Enhet</h4>
        <div style={{ marginBottom: '15px' }}>
          <label htmlFor="reg-mac">MAC-adresse (ID)</label>
          <input id="reg-mac" value={deviceId} onChange={e => setDeviceId(e.target.value)} placeholder="14:2B:2F:E7:11:F9" required />
        </div>
        <div style={{ display: 'flex', gap: '15px' }}>
            <div style={{ flex: 1 }}>
                <label htmlFor="reg-model">Modell</label>
                <input id="reg-model" value={model} onChange={e => setModel(e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
                <label htmlFor="reg-batch">Batch (Nr)</label>
                <input id="reg-batch" value={batchId} onChange={e => setBatchId(e.target.value)} />
            </div>
        </div>
        {error && <p style={{ color: 'var(--danger-color)' }}>{error}</p>}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
          <button type="button" onClick={onCancel} style={{ background: 'transparent', border: '1px solid var(--border-color)', color: 'var(--text-light)' }}>Avbryt</button>
          <button type="submit" disabled={loading}>{loading ? 'Lagrer...' : 'Registrer'}</button>
        </div>
      </form>
    </div>
  );
}

// Full view for device details and editing
function DeviceDetailsForm({ device, onFinished }) {
    const [model, setModel] = useState(device.model || '');
    const [batchId, setBatchId] = useState(device.batchId || '');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const alias = generateAlias(device.id);

    const handleUpdate = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            await callApi('updateDeviceV2', { deviceId: device.id, data: { model, batchId } });
            if (onFinished) onFinished();
        } catch (err) {
            setError('Kunne ikke oppdatere: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (window.confirm(`Slette "${alias}" (${device.id})? Dette kan ikke angres.`)) {
            setLoading(true);
            try {
                await callApi('deleteDeviceV2', { deviceId: device.id });
                if (onFinished) onFinished();
            } catch (err) {
                setError('Feil ved sletting: ' + err.message);
                setLoading(false);
            }
        }
    };

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <form onSubmit={handleUpdate} style={{ background: 'var(--background-light)', color: 'var(--text-light)', padding: '30px', borderRadius: '12px', display: 'flex', flexDirection: 'column', gap: '20px', width: '450px', border: '1px solid var(--border-color)' }}>
                <div style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '15px' }}>
                    <h3 style={{ margin: 0, color: 'var(--primary-accent)' }}>{alias}</h3>
                    <code style={{ fontSize: '12px', color: '#888' }}>ID: {device.id}</code>
                </div>

                <div style={{ display: 'flex', gap: '15px' }}>
                    <div style={{ flex: 1 }}>
                        <label>MODELL</label>
                        <input value={model} onChange={e => setModel(e.target.value)} />
                    </div>
                    <div style={{ flex: 1 }}>
                        <label>BATCH (NR)</label>
                        <input value={batchId} onChange={e => setBatchId(e.target.value)} />
                    </div>
                </div>

                <div style={{ padding: '15px', border: '1px dashed var(--border-color)', marginBottom: '15px', textAlign: 'center', background: 'var(--background-dark)' }}>
                    <label style={{ display: 'block', fontSize: '0.7rem', color: '#8d99ae' }}>CLAIM CODE</label>
                    <code style={{ fontSize: '1.2rem', color: 'var(--primary-accent)' }}>{device.claimCode || 'INGEN KODE'}</code>
                </div>

                <p style={{ fontSize: '0.9rem' }}>Status: <strong>{device.status.toUpperCase()}</strong></p>

                {error && <p style={{ color: 'var(--danger-color)' }}>{error}</p>}

                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
                    <button type="button" onClick={handleDelete} disabled={loading} style={{ backgroundColor: 'var(--danger-color)', color: 'white' }}>Slett Enhet</button>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button type="button" onClick={onFinished} style={{ background: 'transparent', border: '1px solid var(--border-color)', color: 'var(--text-light)' }}>Lukk</button>
                        <button type="submit" disabled={loading} style={{ background: 'var(--primary-accent)', color: 'var(--text-dark)' }}>{loading ? 'Lagrer...' : 'Lagre'}</button>
                    </div>
                </div>
            </form>
        </div>
    );
}

function DeviceRegistry({ allDevices, devicesLoading, refetchAllDevices }) {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [filter, setFilter] = useState('all'); 
  const [showingQrCode, setShowingQrCode] = useState(null);

  const handleFormFinished = () => {
    setShowCreateForm(false);
    setEditingDevice(null);
    refetchAllDevices();
  };

  const filteredDevices = allDevices.filter(d => {
      if (filter === 'unassigned') return d.status === 'unassigned';
      if (filter === 'assigned') return d.status === 'assigned';
      return true;
  });

  const activeStyle = { backgroundColor: 'var(--primary-accent)', color: 'var(--text-dark)', fontWeight: 'bold' };
  const inactiveStyle = { backgroundColor: 'transparent', color: 'var(--text-light)', border: '1px solid var(--border-color)' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h3 style={{ margin: 0 }}>Fysiske Enheter</h3>
            <p style={{ fontSize: '13px', color: '#888', margin: '5px 0 0 0' }}>Administrasjon av varelager og maskinvare.</p>
          </div>
          <button onClick={() => setShowCreateForm(true)} style={{ background: 'var(--primary-accent)', color: 'var(--text-dark)' }}>
            + Registrer Ny Enhet
          </button>
      </div>
      
      <div style={{ marginBottom: '20px' }}>
          <button onClick={() => setFilter('all')} style={filter === 'all' ? activeStyle : inactiveStyle}>Alle ({allDevices.length})</button>
          <button onClick={() => setFilter('unassigned')} style={filter === 'unassigned' ? activeStyle : inactiveStyle}>På Lager ({allDevices.filter(d => d.status === 'unassigned').length})</button>
          <button onClick={() => setFilter('assigned')} style={filter === 'assigned' ? activeStyle : inactiveStyle}>I Bruk ({allDevices.filter(d => d.status === 'assigned').length})</button>
      </div>

      {showCreateForm && <RegisterDeviceForm onDeviceRegistered={handleFormFinished} onCancel={() => setShowCreateForm(false)} />}
      {editingDevice && <DeviceDetailsForm device={editingDevice} onFinished={handleFormFinished} />}

      {showingQrCode && (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
              <div style={{ background: 'var(--background-light)', color: 'var(--text-light)', padding: '40px', borderRadius: '10px', textAlign: 'center', maxWidth: '400px', border: '1px solid var(--border-color)' }}>
                  <h2 style={{ margin: '0 0 10px 0', color: 'var(--primary-accent)' }}>BifrostKey</h2>
                  <h4 style={{ margin: '0', color: '#888' }}>{generateAlias(showingQrCode.id)}</h4>
                  <p style={{ fontSize: '0.8rem', color: '#666', marginBottom: '20px' }}>ID: {showingQrCode.id}</p>
                  
                  <div style={{ padding: '20px', background: 'white', border: '1px solid #eee', display: 'inline-block' }}>
                      <QRCodeSVG 
                        value={`bifrostkey://claim?id=${showingQrCode.id}&code=${showingQrCode.claimCode}`} 
                        size={200}
                      />
                  </div>
                  
                  <div style={{ marginTop: '20px', padding: '10px', background: 'var(--background-dark)', borderRadius: '5px' }}>
                      <span style={{ fontSize: '0.7rem', color: '#888', display: 'block' }}>SETUP CODE</span>
                      <strong style={{ fontSize: '1.2rem', color: 'var(--primary-accent)' }}>{showingQrCode.claimCode}</strong>
                  </div>

                  <div style={{ marginTop: '30px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
                      <button onClick={() => window.print()} style={{ background: 'var(--primary-accent)', color: 'var(--text-dark)' }}>Skriv ut</button>
                      <button onClick={() => setShowingQrCode(null)} style={{ background: 'transparent', border: '1px solid var(--border-color)', color: 'var(--text-light)' }}>Lukk</button>
                  </div>
              </div>
          </div>
      )}

      <table>
        <thead>
          <tr>
            <th>ID (SERIENR)</th>
            <th>NAVN (ALIAS)</th>
            <th>STATUS</th>
            <th style={{ width: '1%', whiteSpace: 'nowrap' }}>HANDLINGER</th>
          </tr>
        </thead>
        <tbody>
          {filteredDevices.map(device => {
            const alias = generateAlias(device.id);
            return (
              <tr key={device.id}>
                <td style={{ fontFamily: 'monospace' }}>{device.id}</td>
                <td><strong>{alias}</strong></td>
                <td>{device.status.toUpperCase()}</td>
                <td style={{ width: '1%', whiteSpace: 'nowrap' }}>
                    <button onClick={() => setEditingDevice(device)} style={{ padding: '8px 12px', fontSize: '0.8rem', marginRight: '5px' }}>Info</button>
                    {device.status === 'unassigned' && (
                        <button onClick={() => setShowingQrCode(device)} style={{ padding: '8px 12px', fontSize: '0.8rem', background: 'var(--secondary-accent)', color: 'white' }}>QR</button>
                    )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default DeviceRegistry;